<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="600px"
    :disabled="store.state.isWorkflowDisabled"
  >
    <v-form ref="entityForm">
      <v-card>
        <v-row class="fluid justify-space-between align-top ma-2">
          <v-card-title class="d-flex align-center">
            <span class="text-h5"><span class="rounded-lg px-1">Create Entity</span></span>
            <help-dialog-workflow-create-entity />
          </v-card-title>
          <v-btn variant="text" icon color="grey" @click="toggleDialog(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-row>
        <v-card-text>
          <v-container grid-list-md>
            <v-radio-group v-model="type">
              <v-radio
                value="ai-workflow"
                :disabled="!workflow.domainEvents || workflow.domainEvents.length === 0"
                data-test="entity-ai-workflow"
              >
                <template v-slot:label>
                  <div class="d-flex align-center">Generate all missing entities based on the workflow&nbsp;<v-icon>mdi-creation</v-icon></div>
                </template>
              </v-radio>
              <div class="fit-content">
                <v-radio value="ai-description">
                  <template v-slot:label>
                    <div class="d-flex align-center">Generate one or many entities based on a provided description&nbsp;<v-icon>mdi-creation</v-icon></div>
                  </template>
                </v-radio>
              </div>
              <div v-if="type === 'ai-description'" class="pl-10"  >
                <v-textarea
                  :rules="[rules.required]"
                  auto-grow
                  rows="3"
                  class="no-border"
                  variant="outlined"
                  v-model="aiDescription"
                  placeholder="Description"
                />
              </div>
              <v-radio label="Start manually" value="empty" data-test="entity-empty-example" />
            </v-radio-group>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" size="large" @click="toggleDialog(false)">Cancel</v-btn>
          <v-btn variant="flat" size="large" data-test="add-entity" @click="addEntity()">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue'
import { useStore } from 'vuex'
import workflowApi from '@/api/workflowApi'
import randomUUID from '@/utils/uuid'
import usePrompts from '@/composables/usePrompts'
import safeStructuredClone from '@/utils/clone'
import { SchemaType } from '@/constants/schema'
import { parseEntityResponse } from '@/shared/utils/workflowSpec'
import HelpDialogWorkflowCreateEntity from '@/components/HelpDialogWorkflowCreateEntity.vue'
import { runAiGeneration } from '@/utils/helpHero'

const { modelValue, entityName } = defineProps({ modelValue: Boolean, entityName: String })
const emit = defineEmits(['update:modelValue'])

const store = useStore()
const { proxy } = getCurrentInstance()
const { getEntitiesFromWorkflow, getEntitiesFromDescription } = usePrompts()

const type = ref('ai-description')
const aiDescription = ref('')
const rules = ref({ required: (value) => !!value || 'Required' })
const entityForm = ref(null)
const workflow = computed(() => store.state.workflow.workflow)

const toggleDialog = (value) => {
  emit('update:modelValue', value)
  if (!value) {
    aiDescription.value = ''
    type.value = 'ai-description'
  }
}

// Entities the AI generates are its saves, not the person's own edits (activation tracking).
const addEntity = () => (type.value === 'ai-workflow' || type.value === 'ai-description' ? runAiGeneration(saveEntity) : saveEntity())

const saveEntity = async () => {
  let newSchemas
  let suggestedEntitiesExist = false

  const validationResult = await entityForm.value.validate()
  if (!validationResult?.valid) {
    return
  }

  if (type.value === 'ai-workflow' || type.value === 'ai-description') {
    if (!workflow.value.aiDescription) {
      await store.dispatch('workflow/openWorkflowDescriptionDialog')
      return
    }

    proxy.$loadingState.start()

    try {
      const res = type.value === 'ai-workflow'
        ? await getEntitiesFromWorkflow()
        : await getEntitiesFromDescription(aiDescription.value)

      if (!res?.entities) {
        store.commit('snackbar/showMessage', {
          content: type.value === 'ai-workflow'
            ? 'The AI was unable to generate entities.'
            : 'The AI was unable to generate entities. Please try adjusting the description.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        proxy.$loadingState.stop()
        return
      }

      newSchemas = safeStructuredClone(workflow.value.schemas || [])
      const existingEntityNames = new Set(newSchemas.filter((s) => s.type === SchemaType.ENTITY).map((s) => s.name.toLowerCase()))

      const seenNames = new Set()
      const newEntities = res.entities.filter((entity) => {
        const lowerName = entity.name.toLowerCase()
        if (existingEntityNames.has(lowerName) || seenNames.has(lowerName)) {
          return false
        }
        seenNames.add(lowerName)
        return true
      })

      if (newEntities.length === 0) {
        suggestedEntitiesExist = true
      } else {
        const entityNameToIdMap = new Map()

        for (const schema of newSchemas) {
          if (schema.type === SchemaType.ENTITY) {
            entityNameToIdMap.set(schema.name.toLowerCase(), schema.id)
          }
        }

        const newEntityUUIDs = new Map()
        for (const entity of newEntities) {
          const uuid = randomUUID()
          newEntityUUIDs.set(entity.name, uuid)
          entityNameToIdMap.set(entity.name.toLowerCase(), uuid)
        }

        for (const entityFromLLM of newEntities) {
          const generatedEntity = parseEntityResponse(entityFromLLM, entityNameToIdMap)
          generatedEntity.id = newEntityUUIDs.get(entityFromLLM.name)
          newSchemas = [...newSchemas, generatedEntity]
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        await store.dispatch('workflow/openRetryDialog', {
          failedItems: [{ type: 'entities', name: 'Generate Entities' }],
          retryCallback: () => addEntity()
        })
      }
      proxy.$loadingState.stop()
      return
    }
  } else if (type.value === 'empty') {
    const existingNames = new Set(
      (workflow.value.schemas || [])
        .filter((s) => s.type === SchemaType.ENTITY)
        .map((s) => s.name.toLowerCase())
    )

    let counter = (workflow.value.schemas?.length || 0) + 1
    let candidateName = entityName || ('New Entity ' + counter)
    while (existingNames.has(candidateName.toLowerCase())) {
      counter++
      candidateName = 'New Entity ' + counter
    }

    newSchemas = [...(workflow.value.schemas || []), {
      name: candidateName,
      type: SchemaType.ENTITY,
      fields: [{
        name: 'NEW FIELD 1',
        cardinality: 'one-to-one',
        exampleData: [''],
        dataType: 'string',
        relatedEntityId: null
      }]
    }]
  }

  if (newSchemas) {
    await workflowApi.updateSchemas({
      schemas: newSchemas?.length > 0 ? newSchemas : undefined,
      workflow: workflow.value
    })

    if (suggestedEntitiesExist) {
      store.commit('snackbar/showMessage', { content: 'All suggested entities already exist!' }, { root: true })
    }
  }

  proxy.$loadingState.stop()
  toggleDialog(false)
}
</script>
